import * as yup from 'yup';
import { SocialMediaOption, socialMediaOptions } from '../../IconPicker/Options';
import { ProfileLink } from '../../../context/ProfileContext';
import { GuardState } from '../../../utils/enums';

const urlSchema = yup.string().url().required();

const SHORTENED_DOMAINS = ['bit.ly', 'tinyurl.com', 't.co', 'ow.ly', 'short.link', 'rb.gy', 'cutt.ly'];

const TRACKING_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'fbclid', 'gclid', 'mc_cid', 'mc_eid',
];

export interface GuardResult {
    state: GuardState;
    key?: string;
    switchTo?: SocialMediaOption | null;
    cleanedUrl?: string;
    showPro?: boolean;
}

const matchesDomain = (hostname: string, domain: string): boolean =>
    hostname === domain || hostname.endsWith('.' + domain);

const hostnameMatchesOption = (hostname: string, option: SocialMediaOption): boolean => {
    if (option.domains.length === 0) return true; // generic — always valid
    return option.domains.some(d => matchesDomain(hostname, d));
};

const findOptionByHostname = (hostname: string): SocialMediaOption | null =>
    socialMediaOptions.find(o =>
        o.domains.length > 0 && o.domains.some(d => matchesDomain(hostname, d))
    ) ?? null;

export const cleanTrackingParams = (url: string): string => {
    try {
        const u = new URL(url);
        TRACKING_PARAMS.forEach(p => u.searchParams.delete(p));
        return u.toString();
    } catch {
        return url;
    }
};

/**
 * Evaluates the SafeLink Guard state for a given URL.
 * Priority: error > duplicate > mismatch > http > shortened > tracking
 */
export const evaluateGuard = (
    url: string,
    selectedOption: SocialMediaOption | null,
    allLinks: ProfileLink[],
    currentLinkId: number,
): GuardResult => {
    if (!url) return { state: GuardState.IDLE };

    if (!urlSchema.isValidSync(url)) {
        return { state: GuardState.ERROR, key: 'guard.invalid' };
    }

    const parsed = new URL(url);
    const { hostname, protocol } = parsed;

    if (allLinks.some(l => l.id_link !== currentLinkId && l.social_url === url)) {
        return { state: GuardState.WARNING, key: 'guard.duplicate' };
    }

    if (selectedOption && !hostnameMatchesOption(hostname, selectedOption)) {
        const detected = findOptionByHostname(hostname);
        return {
            state: GuardState.WARNING,
            key: detected ? 'guard.mismatch' : 'guard.mismatch-unknown',
            switchTo: detected,
        };
    }

    if (protocol === 'http:') {
        return { state: GuardState.WARNING, key: 'guard.http' };
    }

    if (SHORTENED_DOMAINS.some(d => matchesDomain(hostname, d))) {
        return { state: GuardState.WARNING, key: 'guard.shortened', showPro: true };
    }

    if (TRACKING_PARAMS.some(p => parsed.searchParams.has(p))) {
        return { state: GuardState.WARNING, key: 'guard.tracking', cleanedUrl: cleanTrackingParams(url) };
    }

    return { state: GuardState.VALID };
};
